import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Clock,
  Calculator,
  Eye,
  ExternalLink
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import type {
  CompensationValidationResult, 
  ValidationWarning, 
  ValidationError,
  PayrollImpactEstimate 
} from '@/services/payslips/types';
import type { CompensationRule } from '@/services/compensation/types';

interface ValidationRulesListProps {
  validationResults?: CompensationValidationResult;
  isLoading: boolean;
  selectedChanges: Array<{ rule: CompensationRule; type: string }>;
  onViewDetails?: (ruleId: string) => void;
  onResolveConflict?: (issueId: string) => void;
}

export function ValidationRulesList({
  validationResults,
  isLoading,
  selectedChanges,
  onViewDetails,
  onResolveConflict
}: ValidationRulesListProps) {
  const { currencyCode } = useWorkplaceCurrency();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getSeverityIcon = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-orange-600" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-600" />;
    }
  };

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'high':
        return 'border-red-200 bg-red-50 text-red-800';
      case 'medium':
        return 'border-orange-200 bg-orange-50 text-orange-800';
      case 'low':
        return 'border-blue-200 bg-blue-50 text-blue-800';
    }
  };

  const getErrorTypeIcon = (type: ValidationError['type']) => {
    switch (type) {
      case 'conflict':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'invalid_rate':
        return <DollarSign className="h-4 w-4 text-red-600" />;
      case 'missing_data':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
  };

  // Calculate validation summary
  const validationSummary = useMemo(() => {
    if (!validationResults) return null;

    const totalIssues = validationResults.errors.length + validationResults.warnings.length;
    const criticalIssues = validationResults.errors.length;
    const warningIssues = validationResults.warnings.length;
    const highSeverityWarnings = validationResults.warnings.filter(w => w.severity === 'high').length;

    return {
      totalIssues,
      criticalIssues,
      warningIssues,
      highSeverityWarnings,
      isValid: validationResults.isValid,
      canProceed: validationResults.isValid && highSeverityWarnings === 0
    };
  }, [validationResults]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/3"></div>
            <div className="space-y-2">
              <div className="h-3 bg-muted rounded"></div>
              <div className="h-3 bg-muted rounded w-3/4"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!validationResults) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No validation results available.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Validation Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {validationSummary?.isValid ? (
              <CheckCircle className="h-5 w-5 text-green-600" />
            ) : (
              <XCircle className="h-5 w-5 text-red-600" />
            )}
            Validation Summary
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Overall Status */}
          <div className={cn(
            "p-4 rounded-lg border",
            validationSummary?.canProceed 
              ? "border-green-200 bg-green-50" 
              : "border-red-200 bg-red-50"
          )}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold">
                {validationSummary?.canProceed ? 'Ready to Proceed' : 'Issues Found'}
              </span>
              <Badge variant={validationSummary?.canProceed ? 'default' : 'destructive'}>
                {validationSummary?.totalIssues || 0} issues
              </Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              {validationSummary?.canProceed 
                ? 'All compensation changes have passed validation checks.'
                : `${validationSummary?.criticalIssues} critical issues and ${validationSummary?.warningIssues} warnings found.`
              }
            </div>
          </div>

          {/* Issues Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-4 border rounded-lg">
              <XCircle className="h-6 w-6 mx-auto mb-2 text-red-600" />
              <p className="font-semibold text-red-600">{validationSummary?.criticalIssues || 0}</p>
              <p className="text-xs text-muted-foreground">Critical Errors</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <AlertTriangle className="h-6 w-6 mx-auto mb-2 text-orange-600" />
              <p className="font-semibold text-orange-600">{validationSummary?.highSeverityWarnings || 0}</p>
              <p className="text-xs text-muted-foreground">High Priority</p>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <AlertCircle className="h-6 w-6 mx-auto mb-2 text-blue-600" />
              <p className="font-semibold text-blue-600">{(validationSummary?.warningIssues || 0) - (validationSummary?.highSeverityWarnings || 0)}</p>
              <p className="text-xs text-muted-foreground">Low/Medium</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Critical Errors */}
      {validationResults.errors.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <XCircle className="h-5 w-5" />
              Critical Errors ({validationResults.errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {validationResults.errors.map((error, index) => (
              <Alert key={index} className="border-red-200 bg-red-50">
                <div className="flex items-start gap-3">
                  {getErrorTypeIcon(error.type)}
                  <div className="flex-1 space-y-2">
                    <AlertTitle className="text-red-800">
                      {error.type.replace('_', ' ').toUpperCase()}
                    </AlertTitle>
                    <AlertDescription className="text-red-700">
                      {error.message}
                    </AlertDescription>
                    {error.field && (
                      <div className="text-sm text-red-600">
                        <strong>Field:</strong> {error.field}
                      </div>
                    )}
                    {error.affected_employees && error.affected_employees.length > 0 && (
                      <div className="text-sm text-red-600">
                        <strong>Affected:</strong> {error.affected_employees.length} employee(s)
                      </div>
                    )}
                    {onResolveConflict && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onResolveConflict(error.id)}
                        className="mt-2"
                      >
                        Resolve Issue
                      </Button>
                    )}
                  </div>
                </div>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Warnings */}
      {validationResults.warnings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Warnings ({validationResults.warnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {validationResults.warnings.map((warning, index) => (
              <Alert 
                key={index} 
                className={cn("border", getSeverityColor(warning.severity))}
              >
                <div className="flex items-start gap-3">
                  {getSeverityIcon(warning.severity)}
                  <div className="flex-1 space-y-2">
                    <AlertTitle className="capitalize">
                      {warning.type.replace('_', ' ')} - {warning.severity} Priority
                    </AlertTitle>
                    <AlertDescription>
                      {warning.message}
                    </AlertDescription>
                    {warning.affected_employees && warning.affected_employees.length > 0 && (
                      <div className="text-sm opacity-80">
                        <strong>Affected:</strong> {warning.affected_employees.length} employee(s)
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {warning.severity}
                  </Badge>
                </div>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Payroll Impact Estimate */}
      {validationResults.estimated_impact && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Estimated Payroll Impact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Total Cost Change:</span>
                  <span className={cn(
                    "font-bold text-lg",
                    validationResults.estimated_impact.total_cost_change >= 0 
                      ? "text-red-600" 
                      : "text-green-600"
                  )}>
                    {validationResults.estimated_impact.total_cost_change >= 0 ? '+' : ''}
                    {formatCurrency(validationResults.estimated_impact.total_cost_change)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Affected Employees:</span>
                  <span className="font-bold">
                    {validationResults.estimated_impact.affected_employee_count}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium">Period:</span>
                  <Badge variant="outline">
                    {validationResults.estimated_impact.comparison_period}
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Gross Change:</span>
                  <span className="font-mono text-sm">
                    {formatCurrency(validationResults.estimated_impact.estimated_gross_change)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Net Change:</span>
                  <span className="font-mono text-sm">
                    {formatCurrency(validationResults.estimated_impact.estimated_net_change)}
                  </span>
                </div>
                <Separator />
                <div className="text-xs text-muted-foreground">
                  * Estimates based on current compensation data and average work patterns
                </div>
              </div>
            </div>

            {/* Impact Visualization */}
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Impact Level:</span>
                <Badge variant={
                  Math.abs(validationResults.estimated_impact.total_cost_change) > 10000 
                    ? 'destructive' 
                    : Math.abs(validationResults.estimated_impact.total_cost_change) > 5000 
                    ? 'secondary' 
                    : 'outline'
                }>
                  {Math.abs(validationResults.estimated_impact.total_cost_change) > 10000 
                    ? 'High' 
                    : Math.abs(validationResults.estimated_impact.total_cost_change) > 5000 
                    ? 'Medium' 
                    : 'Low'
                  }
                </Badge>
              </div>
              <Progress 
                value={Math.min(Math.abs(validationResults.estimated_impact.total_cost_change) / 1000, 100)} 
                className="h-2"
              />
              <div className="text-xs text-muted-foreground mt-1">
                Impact relative to typical payroll changes
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Affected Changes Details */}
      {selectedChanges.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Changes Being Validated ({selectedChanges.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {selectedChanges.map((change, index) => (
                <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{change.type}</Badge>
                    <div>
                      <p className="font-medium">{change.rule.rule_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {change.rule.rule_type} - Rule Template
                      </p>
                    </div>
                  </div>
                  {onViewDetails && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => onViewDetails(change.rule.id)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Validation Actions */}
      {validationSummary && !validationSummary.canProceed && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Action Required</AlertTitle>
          <AlertDescription>
            Please resolve all critical errors before proceeding with the approval. 
            Review warnings carefully as they may impact payroll calculations.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}